/**
 * Player-initiated state changes. Called from UI event handlers, never from
 * render code and never from tick() - with the single exception of auto-buy,
 * which is the player's own standing instruction.
 */

import { GENERATORS } from '../data/generators.ts';
import { SKILLS_BY_ID } from '../data/skills.ts';
import { CLOCK_MIN, HEAT_MIN } from '../data/balance.ts';
import {
  costOf,
  getModifiers,
  roomToBuy,
  skillAvailable,
  type Modifiers,
} from './formulas.ts';
import type { GameState } from './state.ts';

/** Buys `count` units if affordable. Returns whether the purchase happened. */
export function buyGenerator(state: GameState, index: number, count: number): boolean {
  const def = GENERATORS[index];
  if (def === undefined || count <= 0) return false;

  // The rung below has to carry it. Enforced on every unit, not just the first,
  // or a tier opened with ten Neurons could then be bought without limit.
  const room = roomToBuy(state, index);
  if (room <= 0) return false;
  const wanted = Math.min(count, room);

  const owned = state.generators[index] ?? 0;
  const price = costOf(def, owned, wanted);
  if (state.thoughts.lt(price)) return false;

  state.thoughts = state.thoughts.sub(price);
  state.generators[index] = owned + wanted;
  return true;
}

/** Clamps and applies a clock setting. Returns the value actually taken. */
export function setClock(
  state: GameState,
  clock: number,
  mods: Modifiers = getModifiers(state),
): number {
  const value = Math.round(clock);
  state.clock = value < CLOCK_MIN ? CLOCK_MIN : value > mods.clockCap ? mods.clockCap : value;
  // Latched so the panel stops explaining the lever once it has been used.
  if (state.clock > 100) state.unlocks.overclocked = true;
  return state.clock;
}

/** Dumps coolant. Returns false if the vent is still recharging. */
export function vent(state: GameState, mods: Modifiers = getModifiers(state)): boolean {
  if (state.ventCooldown > 0) return false;
  state.heat = Math.max(HEAT_MIN, state.heat - mods.ventAmount);
  state.ventCooldown = mods.ventCooldown;
  state.unlocks.vented = true;
  return true;
}

/** Buys one rank of a skill. Returns false if unaffordable, owned, or locked. */
export function buySkill(state: GameState, id: string): boolean {
  const def = SKILLS_BY_ID[id];
  if (def === undefined) return false;
  if ((state.skills[id] ?? 0) > 0) return false;
  if (!skillAvailable(state, id)) return false;
  if (state.greyMatter.lt(def.cost)) return false;

  state.greyMatter = state.greyMatter.sub(def.cost);
  state.skills[id] = 1;
  return true;
}

export function setAutoBuy(state: GameState, generatorId: string, enabled: boolean): void {
  state.settings.autoBuy[generatorId] = enabled;
}
